/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios, { AxiosRequestConfig } from 'axios';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';

const buildGetConflictsUrl = (
  workspaceId: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  skillId
    ? urlJoin(
        endpoints.store,
        getAccessPrefixedUrl(
          useNewIAUpdatedAccess,
          assistantId,
          `skills/${skillId}/workspace/recommendations/intents/conflicts`
        )
      )
    : `${endpoints.store}/v1/workspaces/${workspaceId}/recommendations/intents/conflicts`;

const buildGetConflictsForIntentUrl = (
  workspaceId: string,
  intent: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  skillId
    ? urlJoin(
        endpoints.store,
        getAccessPrefixedUrl(
          useNewIAUpdatedAccess,
          assistantId,
          `skills/${skillId}/workspace/intents/${intent}/conflicts`
        )
      )
    : `${endpoints.store}/v1/workspaces/${workspaceId}/intents/${intent}/conflicts`;

const buildGetExampleConflictsUrl = (
  workspaceId: string,
  intent: string,
  id: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  skillId
    ? urlJoin(
        endpoints.store,
        getAccessPrefixedUrl(
          useNewIAUpdatedAccess,
          assistantId,
          `skills/${skillId}/workspace/intents/${intent}/conflicts/${id}`
        )
      )
    : `${endpoints.store}/v1/workspaces/${workspaceId}/intents/${intent}/conflicts/${id}`;

// Get a list of all intents where there are conflicts, and the number of conflicts in each
export function getAllIntentConflicts(
  workspaceId: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const url = buildGetConflictsUrl(
    workspaceId,
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const config: AxiosRequestConfig = {};
  if (skillId) {
    config.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }

  return axios
    .get(url, config)
    .then(({ data: { conflicts = [] } = {}, status }) => {
      if (status === 202) {
        return startPolling(workspaceId, skillId, url);
      }
      return conflicts.reduce((accumulator, { intent, conflicts }) => {
        accumulator[intent] = conflicts;
        return accumulator;
      }, {});
    })
    .catch(error => Promise.reject(error));
}

// Get a list of the examples which have conflicts in a given intent
export function getConflictsForIntent(
  workspaceId: string,
  intentName: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const url = buildGetConflictsForIntentUrl(
    workspaceId,
    intentName,
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );

  const config: AxiosRequestConfig = {};
  if (skillId) {
    config.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }

  return axios
    .get(url, config)
    .then(({ data: { conflicts = [] } = {}, status }) => {
      if (status === 202) {
        return startPolling(workspaceId, skillId, url);
      }
      return conflicts.reduce((accumulator, { example, conflict_id }) => {
        accumulator[example] = conflict_id;
        return accumulator;
      }, {});
    })
    .catch(error => Promise.reject(error));
}

// For a given example, get the intent(s) that conflict
export function getConflictsForExample(
  workspaceId: string,
  intentName: string,
  exampleId: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const url = buildGetExampleConflictsUrl(
    workspaceId,
    intentName,
    exampleId,
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );

  // to get similarity values:
  // pass args, where args = { params : { include_similarity : true }};

  const config: AxiosRequestConfig = {};
  if (skillId) {
    config.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }

  return axios
    .get(url, config)
    .then(({ data, status }) => {
      if (status === 202) {
        return startPollingExamples(workspaceId, skillId, url);
      }
      return data;
    })
    .catch(error => Promise.reject(error));
}

const TIMEOUT_MAX = 210000; // 3 and 1/2 minutes
const TIMEOUT_MAX_INTENT_CONFLICTS = 1200000; // 20 mins;
const ITERATIONS_PER_RATE = 6; // how many times to poll at each refresh rate

function startPollingExamples(workspaceId, skillId, urlPath) {
  const theWorkspaceId = workspaceId;
  const theSkillId = skillId;

  let timePolling = 0;
  let refreshRate = 5000;
  let currentIterations = 0;

  return new Promise((resolve, reject) => {
    let intervalId = setInterval(function callService() {
      currentIterations++;

      if (currentIterations > ITERATIONS_PER_RATE) {
        clearInterval(intervalId);
        currentIterations = 0;
        refreshRate *= 2; // double polling time
        intervalId = setInterval(callService, refreshRate);
        return;
      }

      const config: AxiosRequestConfig = {};
      if (theSkillId) {
        config.headers = { 'X-Watson-Workspace-Id': theWorkspaceId };
      }

      return axios
        .get(urlPath, config)
        .then(({ data, status }) => {
          timePolling += refreshRate;
          if (timePolling >= TIMEOUT_MAX) {
            clearInterval(intervalId);
            const error = { error: 'Timed out' };
            reject(error);
          }

          if (status !== 202) {
            clearInterval(intervalId);
            resolve(data);
          }
        })
        .catch(error => {
          clearInterval(intervalId);
          reject(error);
        });
    }, refreshRate);
  });
}

function startPolling(workspaceId, skillId, urlPath) {
  const theWorkspaceId = workspaceId;
  const theSkillId = skillId;

  let timePolling = 0;
  let refreshRate = 5000;
  let currentIterations = 0;

  return new Promise((resolve, reject) => {
    let intervalId = setInterval(function callService() {
      currentIterations++;

      if (currentIterations > ITERATIONS_PER_RATE) {
        clearInterval(intervalId);
        currentIterations = 0;
        refreshRate *= 2; // double polling time
        intervalId = setInterval(callService, refreshRate);
        return;
      }

      const config: AxiosRequestConfig = {};
      if (theSkillId) {
        config.headers = { 'X-Watson-Workspace-Id': theWorkspaceId };
      }

      return axios
        .get(urlPath, config)
        .then(({ data: { conflicts = [] } = {}, status }) => {
          timePolling += refreshRate;
          if (timePolling >= TIMEOUT_MAX_INTENT_CONFLICTS) {
            clearInterval(intervalId);
            const error = { error: 'Timed out' };
            reject(error);
          }

          if (status !== 202) {
            clearInterval(intervalId);
            resolve(
              conflicts.reduce((accumulator, { intent, conflicts }) => {
                accumulator[intent] = conflicts;
                return accumulator;
              }, {})
            );
          }
        })
        .catch(error => {
          clearInterval(intervalId);
          reject(error);
        });
    }, refreshRate);
  });
}
